import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Radio, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1552174588-6733961c358e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwbWljcm9waG9uZSUyMGRhcmslMjBzdHVkaW98ZW58MHx8fHwxNzg1Mjk4MDk1fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/studio" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/studio");
    else setError(res.error);
  };

  return (
    <div className="vox-noise relative flex min-h-screen items-center justify-center overflow-hidden px-6">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#09090B 0%,rgba(9,9,11,0.7) 50%,#09090B 100%)" }} />

      <Link to="/" className="absolute left-6 top-6 z-10 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition-colors" data-testid="back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Listen
      </Link>

      <div className="relative z-10 w-full max-w-sm vox-fade-up">
        <div className="mb-8 flex items-center gap-2 text-zinc-400">
          <Radio className="h-4 w-4 text-live" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase">VOX·CAST STUDIO</span>
        </div>
        <h1 className="font-serif text-4xl tracking-tight">Broadcaster access</h1>
        <p className="mt-2 mb-8 text-sm text-zinc-500">Sign in to open the studio and go live.</p>

        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-zinc-500">Email</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="login-email"
              className="border-0 border-b border-white/10 bg-transparent px-0 rounded-none focus-visible:ring-0 focus-visible:border-live"
              placeholder="host@voxcast.live"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest text-zinc-500">Password</Label>
            <Input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required data-testid="login-password"
              className="border-0 border-b border-white/10 bg-transparent px-0 rounded-none focus-visible:ring-0 focus-visible:border-live"
              placeholder="••••••••"
            />
          </div>

          {error && <p className="text-sm text-live" data-testid="login-error">{error}</p>}

          <Button
            type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full bg-white text-black hover:bg-zinc-200 transition-colors font-medium"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter Studio"}
          </Button>
        </form>
      </div>
    </div>
  );
}
